import { RefCallback, useCallback, useLayoutEffect, useState, useSyncExternalStore } from 'react';

export type Breakpoint = 'sm' | 'md' | 'lg';

/* Read at runtime from the `--breakpoint-*` custom properties that
 * clay.theme.css emits (via `@theme static`) onto `:root` in the shared
 * clay.compiled.css. Fallbacks mirror that file for environments where the
 * stylesheet isn't applied (jest). */
const FALLBACK_PX: Record<Breakpoint, number> = { sm: 744, md: 1140, lg: 1500 };

const mediaQueries = new Map<Breakpoint, MediaQueryList>();

const getBreakpointPx = (breakpoint: Breakpoint) => {
  const fromCss =
    typeof document === 'undefined'
      ? NaN
      : parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(`--breakpoint-${breakpoint}`),
          10,
        );
  return fromCss || FALLBACK_PX[breakpoint];
};

const getMediaQuery = (breakpoint: Breakpoint) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return undefined;
  }
  let query = mediaQueries.get(breakpoint);
  if (!query) {
    query = window.matchMedia(`(min-width: ${getBreakpointPx(breakpoint)}px)`);
    mediaQueries.set(breakpoint, query);
  }
  return query;
};

const getServerSnapshot = () => true;

export function useBreakpointUp(breakpoint: Breakpoint): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const query = getMediaQuery(breakpoint);
      query?.addEventListener('change', onChange);
      return () => query?.removeEventListener('change', onChange);
    },
    [breakpoint],
  );
  const getSnapshot = useCallback(() => getMediaQuery(breakpoint)?.matches ?? true, [breakpoint]);
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * The container counterpart to `useBreakpointUp`, comparing one element's own width against the
 * same breakpoints. Returns a ref callback for that element, and `true` until it is first measured.
 */
export function useContainerBreakpointUp(
  breakpoint: Breakpoint,
): readonly [RefCallback<HTMLElement>, boolean] {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isUp, setIsUp] = useState(true);

  useLayoutEffect(() => {
    if (element === null || typeof ResizeObserver !== 'function') {
      return undefined;
    }
    const thresholdPx = getBreakpointPx(breakpoint);
    const observer = new ResizeObserver(([entry]) => {
      setIsUp(entry.contentRect.width >= thresholdPx);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, breakpoint]);

  return [setElement, isUp];
}
